/**
 * Header search panel.
 *
 * Two states share one renderer:
 *   - empty query  -> recent searches, curated categories, recently viewed / best sellers
 *   - typed query  -> matching categories, product suggestions, "show all results"
 *
 * Data comes from snippets/search-data.liquid (categories, best sellers),
 * localStorage (recently viewed, recent searches) and Shopify's predictive
 * search endpoint (products).
 */

const STORAGE_KEYS = {
  viewed: 'husky:viewed',
  searches: 'husky:searches',
}

const LIMITS = {
  viewed: 3,
  bestSellers: 6,
  recentSearches: 4,
  categoriesMatched: 3,
  brands: 3,
  categoriesBrowse: 6,
  products: 6,
}

const PANEL_WIDTHS = {
  1: 'lg:w-[min(640px,calc(100vw-48px))]',
  2: 'lg:w-[min(900px,calc(100vw-48px))]',
  3: 'lg:w-[min(1200px,calc(100vw-48px))]',
}

const MIN_QUERY_LENGTH = 2
const DEBOUNCE_MS = 250
const SITE_SEARCH_IDLE_MS = 1200

function readStored(key) {
  try {
    const raw = localStorage.getItem(key)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch (error) {
    return []
  }
}

function writeStored(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
    /* private mode or full quota — the panel degrades to its non-personalised state */
  }
}

function readJsonScript(selector) {
  const node = document.querySelector(selector)
  if (!node) return []

  try {
    const parsed = JSON.parse(node.textContent)
    return Array.isArray(parsed) ? parsed : []
  } catch (error) {
    console.error('Search panel: malformed payload in', selector, error)
    return []
  }
}

function escapeHtml(text) {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

/** Diacritics-insensitive, so "Jacke" matches "Jacken" and "spacak" matches "spacák". */
function normalize(text) {
  return String(text ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
}

const COMPOUND_MIN_QUERY = 6
const COMPOUND_MIN_STEM = 3

/**
 * German shoppers type compounds: the site's most searched term is "softshelljacken",
 * and the category is called "Jacken". The query is longer than the category name, so
 * a plain substring test in either direction alone misses it. Stems drop the plural
 * ending so "Jacken" still reaches "softshelljacken" and "Zelte" reaches "Familienzelt".
 */
function categoryStems(title) {
  return normalize(title)
    .split(/[^a-z0-9]+/)
    .filter((token) => token.length >= 4)
    .map((token) => token.replace(/(en|e|n)$/, ''))
    .filter((stem) => stem.length >= COMPOUND_MIN_STEM)
}

function categoryMatches(category, needle) {
  const haystack = normalize(`${category.title} ${category.context || ''}`)
  if (haystack.includes(needle)) return true
  if (needle.length < COMPOUND_MIN_QUERY) return false

  return categoryStems(category.title).some((stem) => needle.includes(stem))
}

/** Predictive search hands back full-size images; the panel renders them at 48px. */
function thumbnailUrl(url) {
  if (!url || (!url.includes('/cdn/shop/') && !url.includes('cdn.shopify.com'))) return url
  return url.includes('width=') ? url : `${url}${url.includes('?') ? '&' : '?'}width=200`
}

/** Predictive search returns decimal strings, other Shopify JSON returns integer cents. */
function normalizePriceToMajorUnits(rawPrice) {
  if (rawPrice === null || rawPrice === undefined || rawPrice === '') return 0

  if (typeof rawPrice === 'number') {
    return Number.isInteger(rawPrice) ? rawPrice / 100 : rawPrice
  }

  const priceString = String(rawPrice).trim()

  if (priceString.includes('.') || priceString.includes(',')) {
    const normalized = parseFloat(priceString.replace(',', '.'))
    return Number.isFinite(normalized) ? normalized : 0
  }

  const parsedInt = parseInt(priceString, 10)
  return Number.isFinite(parsedInt) ? parsedInt / 100 : 0
}

function formatPrice(rawPrice) {
  const currency = window.shopCurrency?.code || 'EUR'
  const locale = currency === 'CZK' ? 'cs-CZ' : currency === 'EUR' ? 'de-DE' : 'en-US'

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(normalizePriceToMajorUnits(rawPrice))
}

/**
 * Records a product view so the panel can lead with it on the next focus.
 * Called from product.js on every PDP; `price` arrives raw and is formatted here.
 */
function recordProductView(product) {
  if (!product || !product.url) return

  const entry = {
    title: product.title,
    url: product.url,
    image: product.image || '',
    price: product.price === undefined || product.price === '' ? '' : formatPrice(product.price),
  }

  const rest = readStored(STORAGE_KEYS.viewed).filter((item) => item.url !== entry.url)
  writeStored(STORAGE_KEYS.viewed, [entry, ...rest].slice(0, 12))
}

function recordSearchTerm(term) {
  const trimmed = String(term || '').trim()
  if (trimmed.length < MIN_QUERY_LENGTH) return

  const rest = readStored(STORAGE_KEYS.searches).filter((item) => normalize(item) !== normalize(trimmed))
  writeStored(STORAGE_KEYS.searches, [trimmed, ...rest].slice(0, LIMITS.recentSearches))
}

class HeaderSearch {
  constructor(inputs) {
    this.inputs = inputs
    this.overlay = document.querySelector('[data-search-overlay]')
    this.panel = this.overlay.querySelector('[data-search-panel]')
    this.content = this.overlay.querySelector('[data-search-panel-content]')
    this.header = document.querySelector('[data-header]')

    this.translations = window.__translations || {}
    this.categories = HeaderSearch.categories
    this.bestSellers = HeaderSearch.bestSellers
    this.phrases = readJsonScript('[data-search-phrases]')
    this.brands = readJsonScript('[data-search-brands]')

    this.query = ''
    this.products = []
    this.activeIndex = -1
    this.debounceTimer = null
    this.siteSearchTimer = null
    this.requestToken = 0

    this.bindEvents()
  }

  static get categories() {
    if (!this._categories) this._categories = readJsonScript('[data-search-categories]')
    return this._categories
  }

  static get bestSellers() {
    if (!this._bestSellers) this._bestSellers = readJsonScript('[data-search-bestsellers]')
    return this._bestSellers
  }

  t(key, fallback) {
    return key.split('.').reduce((value, part) => value?.[part], this.translations) || fallback
  }

  /** The input the visitor is actually using; both share one overlay. */
  get input() {
    return this.activeInput || this.inputs[0]
  }

  bindEvents() {
    this.inputs.forEach((input) => {
      input.addEventListener('focus', () => {
        this.activeInput = input
        this.open()
      })
      input.addEventListener('input', () => {
        this.activeInput = input
        this.handleInput()
      })
      input.addEventListener('keydown', (event) => this.handleKeydown(event))

      const form = input.closest('form')
      if (form) {
        form.addEventListener('submit', () => {
          const term = input.value.trim()
          recordSearchTerm(term)
          this.trackSiteSearch(term)
        })
      }
    })

    /**
     * The header paints above the scrim so the input stays usable, which means a
     * click on the logo, the nav or the cart never reaches the scrim. Treat the
     * header as outside the panel too — except the search field itself, which is
     * what opened it.
     *
     * Capture phase matters: the mobile menu and the cart drawer lock body scroll
     * from their own click handlers, and closing after them would release a lock
     * they still need. Running first lets them re-lock on the way down.
     */
    if (this.header) {
      this.header.addEventListener(
        'click',
        (event) => {
          if (!this.isOpen) return
          if (event.target.closest('[data-search-wrapper], [data-search-wrapper-mobile]')) return
          this.close()
        },
        true
      )
    }

    window.addEventListener('resize', () => {
      if (this.isOpen) this.positionPanel()
    })

    this.overlay.querySelector('[data-search-scrim]').addEventListener('click', () => this.close())
    this.overlay.querySelector('[data-search-close]').addEventListener('click', () => this.close())
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) this.close()
    })

    this.content.addEventListener('click', (event) => {
      const item = event.target.closest('[data-search-item]')
      if (item) {
        this.handleItemClick(item)
        return
      }

      if (event.target.closest('[data-search-clear-recent]')) {
        event.preventDefault()
        writeStored(STORAGE_KEYS.searches, [])
        this.render()
      }
    })
  }

  open() {
    if (this.isOpen) {
      this.render()
      return
    }

    this.query = this.input.value.trim()
    this.render()
    this.positionPanel()
    this.overlay.classList.remove('hidden')
    this.inputs.forEach((input) => input.setAttribute('aria-expanded', 'true'))
    document.body.style.overflow = 'hidden'
  }

  close() {
    this.overlay.classList.add('hidden')
    this.inputs.forEach((input) => input.setAttribute('aria-expanded', 'false'))
    document.body.style.overflow = ''
    this.setActiveIndex(-1)
  }

  /**
   * Hang the panel off the header's real bottom edge, which moves with the
   * announcement bar. Mobile needs this too: the header paints above the overlay,
   * so a panel pinned to the viewport top would sit behind it.
   */
  positionPanel() {
    const bottom = this.header ? this.header.getBoundingClientRect().bottom : 0
    const gap = window.innerWidth >= 1024 ? 8 : 0
    this.panel.style.top = `${Math.max(bottom + gap, 0)}px`
  }

  get isOpen() {
    return !this.overlay.classList.contains('hidden')
  }

  handleInput() {
    this.query = this.input.value.trim()
    clearTimeout(this.debounceTimer)
    clearTimeout(this.siteSearchTimer)

    if (this.query.length < MIN_QUERY_LENGTH) {
      this.products = []
      this.open()
      return
    }

    this.debounceTimer = setTimeout(() => this.performSearch(this.query), DEBOUNCE_MS)
  }

  async performSearch(query) {
    const token = ++this.requestToken

    try {
      const response = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=${LIMITS.products}`
      )
      if (!response.ok) throw new Error(`Predictive search responded ${response.status}`)

      const data = await response.json()
      if (token !== this.requestToken) return

      this.products = data.resources?.results?.products || []
      this.open()

      this.siteSearchTimer = setTimeout(() => this.trackSiteSearch(query), SITE_SEARCH_IDLE_MS)
    } catch (error) {
      if (token !== this.requestToken) return
      console.error('Search error:', error)
      this.content.innerHTML = `<p class='px-4 py-8 text-center text-[14px] text-[#666666]'>${escapeHtml(
        this.t('search.search_error', 'Chyba při vyhledávání')
      )}</p>`
    }
  }

  /**
   * The browse list takes one category per parent before taking a second, so the
   * main-menu fallback cannot fill all six slots with menswear. A curated
   * 'search-categories' menu carries no context and keeps its authored order.
   */
  browseCategories() {
    const byContext = new Map()

    this.categories.forEach((category) => {
      const key = category.context || ''
      if (!byContext.has(key)) byContext.set(key, [])
      byContext.get(key).push(category)
    })

    const groups = Array.from(byContext.values())
    const picked = []

    for (let round = 0; picked.length < LIMITS.categoriesBrowse; round += 1) {
      const before = picked.length

      for (const group of groups) {
        if (picked.length >= LIMITS.categoriesBrowse) break
        if (group[round]) picked.push(group[round])
      }

      if (picked.length === before) break
    }

    return picked
  }

  matchedCategories(query) {
    const needle = normalize(query)
    if (!needle) return []

    return this.categories.filter((category) => categoryMatches(category, needle)).slice(0, LIMITS.categoriesMatched)
  }

  render() {
    this.content.innerHTML = this.query.length >= MIN_QUERY_LENGTH ? this.queryStateHtml() : this.emptyStateHtml()
    this.applyWidth()
    this.setActiveIndex(-1)
  }

  /** Width follows the column count, so a lone results list is not stranded in a 1200px shell. */
  applyWidth() {
    const columns = this.content.querySelectorAll('[data-search-column]').length || 1
    const width = PANEL_WIDTHS[Math.min(columns, 3)]

    Object.values(PANEL_WIDTHS).forEach((candidate) => this.panel.classList.remove(candidate))
    this.panel.classList.add(width)
  }

  emptyStateHtml() {
    const recentSearches = readStored(STORAGE_KEYS.searches)
    const viewed = readStored(STORAGE_KEYS.viewed).slice(0, LIMITS.viewed)
    const viewedUrls = new Set(viewed.map((item) => item.url))
    const bestSellers = this.bestSellers.filter((item) => !viewedUrls.has(item.url))
    const hero = this.bestSellers.find((item) => item.hero)

    const rail = []

    if (recentSearches.length) {
      rail.push(
        this.sectionHtml(
          this.t('search.panel_recent_searches', 'Poslední hledání'),
          this.chipsHtml(
            recentSearches.map((term) => ({ title: term, url: `/search?q=${encodeURIComponent(term)}` })),
            'recent'
          ),
          `<button type='button' data-search-clear-recent class='text-[12px] text-[#666666] underline transition-opacity hover:opacity-70'>${escapeHtml(
            this.t('search.panel_clear', 'Smazat')
          )}</button>`
        )
      )
    }

    if (this.phrases.length) {
      rail.push(
        this.sectionHtml(this.t('search.panel_phrases', 'Populární dotazy'), this.chipsHtml(this.phrases, 'phrase'))
      )
    }

    if (this.categories.length) {
      rail.push(
        this.sectionHtml(this.t('search.panel_categories', 'Kategorie'), this.categoryListHtml(this.browseCategories()))
      )
    }

    if (this.brands.length) {
      rail.push(this.sectionHtml(this.t('search.panel_brands', 'Značky'), this.linkListHtml(this.brands, 'brand')))
    }

    const main = []
    if (viewed.length) {
      main.push(
        this.sectionHtml(
          this.t('search.panel_recently_viewed', 'Naposledy prohlížené'),
          this.storedProductListHtml(viewed, 'viewed')
        )
      )
    }
    if (bestSellers.length) {
      main.push(
        this.sectionHtml(
          this.t('search.panel_best_sellers', 'Nejprodávanější'),
          this.storedProductListHtml(bestSellers.slice(0, LIMITS.bestSellers), 'bestseller')
        )
      )
    }

    return this.layoutHtml(rail.join(''), hero ? this.heroHtml(hero) : '', main.join(''))
  }

  /**
   * Rail | hero | results on desktop. The hero column disappears while typing and
   * on narrow screens, where all three stack in reading order.
   */
  /**
   * Only columns with content are rendered. A query that matches no category and
   * no brand used to leave an empty tinted rail sitting next to the results.
   */
  layoutHtml(rail, hero, main) {
    const present = [
      rail && `<div class='bg-secondary/40 lg:border-r lg:border-border' data-search-column>${rail}</div>`,
      hero && `<div class='lg:border-r lg:border-border' data-search-column>${hero}</div>`,
      main && `<div data-search-column>${main}</div>`,
    ].filter(Boolean)

    if (present.length < 2) return present.join('')

    const columns =
      present.length === 3
        ? 'lg:grid-cols-[minmax(0,17rem)_minmax(0,19rem)_minmax(0,1fr)]'
        : rail
          ? 'lg:grid-cols-[minmax(0,17rem)_minmax(0,1fr)]'
          : 'lg:grid-cols-[minmax(0,19rem)_minmax(0,1fr)]'

    return `<div class='lg:grid ${columns}'>${present.join('')}</div>`
  }

  /**
   * Tile and photo follow product-card.liquid exactly: a #f2f2f2 tile with
   * brightness-95 on the image. Every product photo here carries a baked-in white
   * background (even the PNGs are fully opaque) and aspect ratios are not
   * normalised, so an undimmed photo letterboxes into a visible white rectangle.
   * 255 × 0.95 = 242 = #f2f2f2, which is why the two values have to move together.
   */
  heroHtml(product) {
    return `
      <section class='px-4 py-2.5'>
        <h3 class='mb-1.5 text-[12px] uppercase tracking-[0.06em] text-[#666666]'>${escapeHtml(
          this.t('search.panel_top_product', 'Tip z nabídky')
        )}</h3>
        <a
          href='${escapeHtml(product.url)}'
          data-search-item
          data-search-kind='hero'
          class='block transition-opacity hover:opacity-90'
        >
          <span class='mb-3 block overflow-hidden rounded-lg bg-[#f2f2f2]'>
            <img src='${escapeHtml(product.hero)}' alt='${escapeHtml(
              product.title
            )}' class='aspect-square w-full object-contain brightness-95' loading='lazy'>
          </span>
          <span class='block text-[16px] text-primary'>${escapeHtml(product.price)}</span>
          <span class='mt-1 block text-[14px] text-[#666666]'>${escapeHtml(product.title)}</span>
        </a>
      </section>
    `
  }

  chipsHtml(items, kind) {
    return `<div class='flex flex-wrap gap-2'>${items
      .map(
        (item) => `
        <a
          href='${escapeHtml(item.url)}'
          data-search-item
          data-search-kind='${kind}'
          data-search-term='${escapeHtml(item.title)}'
          class='inline-flex items-center rounded-full border border-border bg-background px-3 py-1.5 text-[14px] text-primary transition-colors hover:bg-secondary'
        >${escapeHtml(item.title)}</a>`
      )
      .join('')}</div>`
  }

  linkListHtml(items, kind) {
    return `<div class='-mx-2'>${items
      .map(
        (item) => `
        <a
          href='${escapeHtml(item.url)}'
          data-search-item
          data-search-kind='${kind}'
          class='block truncate rounded-lg px-2 py-1.5 text-[14px] text-primary transition-colors hover:bg-secondary'
        >${escapeHtml(item.title)}</a>`
      )
      .join('')}</div>`
  }

  queryStateHtml() {
    const categories = this.matchedCategories(this.query)
    const needle = normalize(this.query)
    const brands = this.brands.filter((brand) => normalize(brand.title).includes(needle)).slice(0, LIMITS.brands)

    if (!categories.length && !brands.length && !this.products.length) {
      return `<p class='px-4 py-8 text-center text-[14px] text-[#666666]'>${escapeHtml(
        this.t('search.no_results_live', 'Nenalezeny žádné produkty pro')
      )} „${escapeHtml(this.query)}“</p>`
    }

    const rail = []
    if (categories.length) {
      rail.push(this.sectionHtml(this.t('search.panel_categories', 'Kategorie'), this.categoryListHtml(categories)))
    }
    if (brands.length) {
      rail.push(this.sectionHtml(this.t('search.panel_brands', 'Značky'), this.linkListHtml(brands, 'brand')))
    }

    const main = this.products.length
      ? this.sectionHtml(this.t('search.panel_products', 'Produkty'), this.suggestedProductListHtml(this.products))
      : ''

    const footer = `
      <a
        href='/search?q=${encodeURIComponent(this.query)}'
        data-search-item
        data-search-kind='show-all'
        class='block border-t border-border px-4 py-3 text-center text-[14px] text-primary transition-opacity hover:opacity-70'
      >${escapeHtml(this.t('search.show_all_results', 'Zobrazit všechny výsledky'))}</a>
    `

    return this.layoutHtml(rail.join(''), '', main) + footer
  }

  sectionHtml(title, body, action = '') {
    return `
      <section class='border-b border-border px-4 py-2.5 last:border-b-0'>
        <div class='mb-1.5 flex items-center justify-between'>
          <h3 class='text-[12px] uppercase tracking-[0.06em] text-[#666666]'>${escapeHtml(title)}</h3>
          ${action}
        </div>
        ${body}
      </section>
    `
  }

  categoryListHtml(categories) {
    return `<div class='-mx-2'>${categories
      .map(
        (category) => `
        <a
          href='${escapeHtml(category.url)}'
          data-search-item
          data-search-kind='category'
          class='flex items-center gap-3 rounded-lg px-2 py-1.5 text-[14px] text-primary transition-colors hover:bg-secondary'
        >
          ${
            category.image
              ? `<span class='h-9 w-9 shrink-0 overflow-hidden rounded-md bg-[#f2f2f2]'>
                   <img src='${escapeHtml(thumbnailUrl(category.image))}' alt='' class='h-full w-full object-contain brightness-95' loading='lazy' width='36' height='36'>
                 </span>`
              : ''
          }
          <span class='min-w-0 flex-1 truncate'>${escapeHtml(category.title)}</span>
          ${
            category.context
              ? `<span class='shrink-0 text-[12px] text-[#666666]'>${escapeHtml(category.context)}</span>`
              : ''
          }
        </a>`
      )
      .join('')}</div>`
  }

  storedProductListHtml(products, kind) {
    return this.productListHtml(
      products.map((product) => ({
        title: product.title,
        url: product.url,
        image: thumbnailUrl(product.image),
        price: product.price,
      })),
      kind
    )
  }

  suggestedProductListHtml(products) {
    return this.productListHtml(
      products.map((product) => ({
        title: product.title,
        url: product.url || `/products/${product.handle}`,
        image: this.getProductImageUrl(product),
        price: formatPrice(product.price),
      })),
      'product'
    )
  }

  productListHtml(products, kind) {
    return `<div class='-mx-2'>${products
      .map(
        (product) => `
        <a
          href='${escapeHtml(product.url)}'
          data-search-item
          data-search-kind='${kind}'
          class='flex items-center gap-3 rounded-lg px-2 py-1.5 transition-colors hover:bg-secondary'
        >
          <div class='h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-[#f2f2f2]'>
            ${
              product.image
                ? `<img src='${escapeHtml(product.image)}' alt='${escapeHtml(
                    product.title
                  )}' class='h-full w-full object-contain brightness-95' loading='lazy' width='48' height='48'>`
                : ''
            }
          </div>
          <div class='min-w-0 flex-1'>
            <p class='truncate text-[14px] text-primary'>${escapeHtml(product.title)}</p>
            ${product.price ? `<p class='text-[14px] text-[#666666]'>${escapeHtml(product.price)}</p>` : ''}
          </div>
        </a>`
      )
      .join('')}</div>`
  }

  get items() {
    return Array.from(this.content.querySelectorAll('[data-search-item]'))
  }

  setActiveIndex(index) {
    const items = this.items
    this.activeIndex = index

    items.forEach((item, position) => {
      const isActive = position === index
      item.classList.toggle('bg-secondary', isActive)
      item.setAttribute('aria-selected', isActive ? 'true' : 'false')
    })

    if (index >= 0 && items[index]) {
      items[index].scrollIntoView({ block: 'nearest' })
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.close()
      return
    }

    if (!this.isOpen) return

    const items = this.items
    if (!items.length) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      this.setActiveIndex((this.activeIndex + 1) % items.length)
      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      this.setActiveIndex(this.activeIndex <= 0 ? items.length - 1 : this.activeIndex - 1)
      return
    }

    if (event.key === 'Enter' && this.activeIndex >= 0) {
      event.preventDefault()
      const item = items[this.activeIndex]
      this.handleItemClick(item)
      window.location.href = item.getAttribute('href')
    }
  }

  handleItemClick(item) {
    const kind = item.dataset.searchKind

    if (kind === 'recent') {
      recordSearchTerm(item.dataset.searchTerm)
      return
    }

    if (this.query.length >= MIN_QUERY_LENGTH) {
      clearTimeout(this.siteSearchTimer)
      recordSearchTerm(this.query)
      this.trackSiteSearch(this.query)
    }

    trackEvent('Search panel', kind, this.query || '(empty query)')
  }

  /**
   * Matomo site search, once per distinct term per pageview. Callers decide when:
   * on submit or result click immediately, otherwise after the term settles.
   * Firing per keystroke would bury the real terms under their own prefixes.
   */
  trackSiteSearch(term) {
    const trimmed = String(term || '').trim()
    if (trimmed.length < MIN_QUERY_LENGTH) return

    const key = normalize(trimmed)
    if (HeaderSearch.trackedTerms.has(key)) return
    HeaderSearch.trackedTerms.add(key)

    const resultCount = this.query === trimmed ? this.products.length : false
    pushMatomo(['trackSiteSearch', trimmed, 'Header panel', resultCount])
  }

  getProductImageUrl(product) {
    const candidates = [product?.featured_image, product?.image, product?.images?.[0]]

    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.length) return thumbnailUrl(candidate)
      if (candidate && typeof candidate === 'object' && candidate.url) return thumbnailUrl(candidate.url)
    }

    return ''
  }
}

HeaderSearch.trackedTerms = new Set()

/** Matomo is injected by GTM, so the queue may not exist yet on early interactions. */
function pushMatomo(payload) {
  window._paq = window._paq || []
  window._paq.push(payload)
}

function trackEvent(category, action, name) {
  pushMatomo(['trackEvent', category, action || 'unknown', name])
}

function initHeaderSearch() {
  const inputs = [
    document.querySelector('[data-search-input]'),
    document.querySelector('[data-search-input-mobile]'),
  ].filter(Boolean)

  if (!inputs.length || !document.querySelector('[data-search-overlay]')) return

  new HeaderSearch(inputs)
  window.HuskySearch = { recordProductView }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHeaderSearch)
} else {
  initHeaderSearch()
}
